import type { CodeSnippetLang, ConsoleOperation } from './types';

export const CODE_SNIPPET_LANGS: CodeSnippetLang[] = ['curl', 'python', 'r', 'javascript'];

export const CODE_SNIPPET_LABELS: Record<CodeSnippetLang, string> = {
  curl: 'cURL',
  python: 'Python',
  r: 'R',
  javascript: 'JavaScript',
};

const HTTR_VERBS = new Set(['get', 'post', 'put', 'patch', 'delete', 'head']);

export type MultipartSnippetField = {
  name: string;
  value?: string;
  isFile?: boolean;
};

function escapeRSingleQuoted(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

function activeMultipartFields(fields: MultipartSnippetField[]): MultipartSnippetField[] {
  return fields.filter(field => field.isFile || Boolean(field.value?.trim()));
}

export function buildCodeSnippets(options: {
  method: ConsoleOperation['method'];
  url: string;
  apiKey: string;
  accept?: string;
  contentType?: string;
  body?: string;
  multipart?: MultipartSnippetField[];
}): Record<CodeSnippetLang, string> {
  const {
    method,
    url,
    apiKey,
    accept = '*/*',
    contentType = 'application/json',
    body,
    multipart,
  } = options;
  const keyPlaceholder = apiKey || 'YOUR_API_KEY';
  const upper = method.toUpperCase();
  const multipartFields = multipart ? activeMultipartFields(multipart) : [];
  const isMultipart = multipartFields.length > 0;
  const hasBody = !isMultipart && Boolean(body && ['post', 'put', 'patch'].includes(method));

  if (isMultipart) {
    return buildMultipartSnippets({
      method,
      upper,
      url,
      keyPlaceholder,
      accept,
      fields: multipartFields,
    });
  }

  const curlParts = [
    `curl -X ${upper} '${url}' \\`,
    `  -H 'Authorization: ${keyPlaceholder}' \\`,
    `  -H 'accept: ${accept}'`,
  ];
  if (hasBody) {
    curlParts[curlParts.length - 1] += ' \\';
    curlParts.push(`  -H 'Content-Type: ${contentType}' \\`);
    curlParts.push(`  -d '${body!.replace(/'/g, `'\\''`)}'`);
  }

  const python = [
    'import requests',
    '',
    `url = "${url}"`,
    'headers = {',
    `    "Authorization": "${keyPlaceholder}",`,
    `    "accept": "${accept}",`,
  ];
  if (hasBody) {
    python.push(`    "Content-Type": "${contentType}",`);
  }
  python.push('}');
  if (hasBody) {
    python.push('', `body = """${body}"""`, '');
    python.push(`response = requests.${method}(url, headers=headers, data=body, verify=True)`);
  } else {
    python.push('', `response = requests.${method}(url, headers=headers, verify=True)`);
  }
  python.push('print(response.status_code)', 'print(response.text)');

  const jsHeaderLines = [`    Authorization: "${keyPlaceholder}",`, `    accept: "${accept}",`];
  if (hasBody) {
    jsHeaderLines.push(`    "Content-Type": "${contentType}",`);
  }

  const javascript = [
    `const response = await fetch("${url}", {`,
    `  method: "${upper}",`,
    '  headers: {',
    ...jsHeaderLines,
    '  }' + (hasBody ? `,` : ''),
  ];
  if (hasBody) {
    javascript.push(`  body: ${JSON.stringify(body)},`);
  }
  javascript.push('});', '', 'const text = await response.text();', 'console.log(response.status, text);');

  const rHeaders = [
    `  "Authorization" = "${keyPlaceholder}",`,
    `  "accept" = "${accept}"` + (hasBody ? ',' : ''),
  ];
  if (hasBody) {
    rHeaders.push(`  "Content-Type" = "${contentType}"`);
  }
  const rCall = HTTR_VERBS.has(method)
    ? `${upper}(url, add_headers(.headers = headers)${hasBody ? ', body = body, encode = "raw"' : ''})`
    : `VERB("${upper}", url, add_headers(.headers = headers)${hasBody ? ', body = body, encode = "raw"' : ''})`;
  const r = [
    'library(httr)',
    '',
    `url <- "${url}"`,
    'headers <- c(',
    ...rHeaders,
    ')',
  ];
  if (hasBody) {
    r.push('', `body <- '${escapeRSingleQuoted(body!)}'`);
  }
  r.push(
    '',
    `response <- ${rCall}`,
    'print(status_code(response))',
    'print(content(response, as = "text", encoding = "UTF-8"))',
  );

  return {
    curl: curlParts.join('\n'),
    python: python.join('\n'),
    r: r.join('\n'),
    javascript: javascript.join('\n'),
  };
}

function buildMultipartSnippets(options: {
  method: ConsoleOperation['method'];
  upper: string;
  url: string;
  keyPlaceholder: string;
  accept: string;
  fields: MultipartSnippetField[];
}): Record<CodeSnippetLang, string> {
  const { method, upper, url, keyPlaceholder, accept, fields } = options;

  const curlParts = [
    `curl -X ${upper} '${url}' \\`,
    `  -H 'Authorization: ${keyPlaceholder}' \\`,
    `  -H 'accept: ${accept}' \\`,
  ];
  fields.forEach((field, index) => {
    const suffix = index === fields.length - 1 ? '' : ' \\';
    const formValue = field.isFile
      ? field.value?.startsWith('@')
        ? field.value
        : `@${field.value || 'your-file.txt'}`
      : field.value ?? '';
    curlParts.push(`  -F '${field.name}=${formValue}'${suffix}`);
  });

  const pythonFiles: string[] = [];
  const pythonData: string[] = [];
  for (const field of fields) {
    if (field.isFile) {
      const path = (field.value || '@your-file.txt').replace(/^@/, '');
      pythonFiles.push(`    "${field.name}": open("${path}", "rb"),`);
    } else if (field.value) {
      pythonData.push(`    "${field.name}": "${field.value.replace(/"/g, '\\"')}",`);
    }
  }

  const python = [
    'import requests',
    '',
    `url = "${url}"`,
    'headers = {',
    `    "Authorization": "${keyPlaceholder}",`,
    `    "accept": "${accept}",`,
    '}',
  ];
  if (pythonData.length) {
    python.push('', 'data = {', ...pythonData, '}');
  }
  if (pythonFiles.length) {
    python.push('', 'files = {', ...pythonFiles, '}');
  }
  const pyArgs = ['url', 'headers=headers'];
  if (pythonData.length) pyArgs.push('data=data');
  if (pythonFiles.length) pyArgs.push('files=files');
  pyArgs.push('verify=True');
  python.push('', `response = requests.${method}(${pyArgs.join(', ')})`);
  python.push('print(response.status_code)', 'print(response.text)');

  const js = [
    'const formData = new FormData();',
    ...fields.map(field => {
      if (field.isFile) {
        return `// formData.append("${field.name}", fileInput.files[0]);`;
      }
      return `formData.append("${field.name}", ${JSON.stringify(field.value ?? '')});`;
    }),
    '',
    `const response = await fetch("${url}", {`,
    `  method: "${upper}",`,
    '  headers: {',
    `    Authorization: "${keyPlaceholder}",`,
    `    accept: "${accept}",`,
    '  },',
    '  body: formData,',
    '});',
    '',
    'const text = await response.text();',
    'console.log(response.status, text);',
  ];

  const rBodyLines = fields.map(field => {
    if (field.isFile) {
      const path = (field.value || '@your-file.txt').replace(/^@/, '');
      return `  "${field.name}" = upload_file("${path}")`;
    }
    return `  "${field.name}" = "${escapeRSingleQuoted(field.value ?? '')}"`;
  });
  const rVerb = HTTR_VERBS.has(method) ? upper : `VERB("${upper}"`;
  const rCall = HTTR_VERBS.has(method)
    ? `${rVerb}(url, add_headers(.headers = headers), body = body, encode = "multipart")`
    : `VERB("${upper}", url, add_headers(.headers = headers), body = body, encode = "multipart")`;

  const r = [
    'library(httr)',
    '',
    `url <- "${url}"`,
    'headers <- c(',
    `  "Authorization" = "${keyPlaceholder}",`,
    `  "accept" = "${accept}"`,
    ')',
    '',
    'body <- list(',
    rBodyLines.join(',\n'),
    ')',
    '',
    `response <- ${rCall}`,
    'print(status_code(response))',
    'print(content(response, as = "text", encoding = "UTF-8"))',
  ];

  return {
    curl: curlParts.join('\n'),
    python: python.join('\n'),
    r: r.join('\n'),
    javascript: js.join('\n'),
  };
}

export const SAMPLE_RESPONSE = {
  status: 'OK',
  paging: {
    totalElements: 2,
    totalPages: 1,
    currentPage: 0,
  },
  payload: [
    {
      symbolOfGene: 'APOE',
      geneNcbiID: 348,
      diseaseName: "Alzheimer's Disease",
      diseaseUMLSCUI: 'C0002395',
      score: 0.9,
      ei: 0.978,
      el: 'Definitive',
      numPMIDs: 2145,
    },
    {
      symbolOfGene: 'PSEN1',
      geneNcbiID: 5663,
      diseaseName: "Alzheimer's Disease",
      diseaseUMLSCUI: 'C0002395',
      score: 0.88,
      ei: 0.95,
      el: 'Definitive',
      numPMIDs: 1280,
    },
  ],
};
